/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { cn } from '@/shared/ui/utils/tailwind';
import type { FacebookSettings } from './facebook-settings.helpers';
import { FF_APP_FACEBOOK_ADS } from '@/shared/utils/feature-flag';
import { Text, Title, UnstyledButton } from '@/shared/ui';
import type { StatusBadgeTone } from '@/shared/ui';

/**
 * Facebook brand glyph rendered as an inline SVG.
 *
 * @param {object} props - Component props.
 * @param {number} [props.size] - Icon width/height in pixels.
 * @param {string} [props.color] - Fill color; defaults to the Facebook brand blue. Pass "#fff" when placed on a same-color background.
 * @returns {JSX.Element} The Facebook icon.
 */
export const FbIcon: React.FC<{ size?: number; color?: string }> = ({ size = 16, color = '#1877F2' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={color} className="shrink-0">
    <path d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z" />
  </svg>
);

/**
 * Instagram brand glyph rendered as an inline SVG.
 *
 * @param {object} props - Component props.
 * @param {number} [props.size] - Icon width/height in pixels.
 * @param {string} [props.color] - Fill color; defaults to the Instagram brand pink. Pass "#fff" when placed on a same-color background.
 * @returns {JSX.Element} The Instagram icon.
 */
export const IgIcon: React.FC<{ size?: number; color?: string }> = ({ size = 16, color = '#E4405F' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={color} className="shrink-0">
    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
  </svg>
);

/**
 * Accessible on/off switch styled as a sliding toggle.
 *
 * @param {object} props - Component props.
 * @param {boolean} props.checked - Whether the toggle is on.
 * @param {number} [props.size] - Knob/track height in pixels.
 * @param {string} [props.testId] - Optional test id.
 * @param {() => void} [props.onClick] - Click handler.
 * @param {boolean} [props.disabled] - Whether the toggle is disabled.
 * @returns {JSX.Element} The rendered toggle switch.
 */
export const Toggle: React.FC<{
  checked: boolean;
  size?: number;
  testId?: string;
  onClick?: () => void;
  disabled?: boolean;
}> = ({ checked, size = 20, testId, onClick, disabled = false }) => {
  const trackW = Math.round(size * 1.9);
  const pad = 2;
  const knob = size - pad * 2;
  const travel = trackW - pad * 2 - knob;
  return (
    <UnstyledButton
      type="button"
      data-testid={testId}
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      className={cn(
        'relative inline-flex items-center shrink-0 rounded-full border-none transition-colors duration-200 mt-(--space-0-5)',
        disabled ? 'cursor-not-allowed opacity-40' : 'cursor-pointer',
        checked && !disabled ? 'bg-(--seed-base)' : 'bg-(--border-subtle)',
      )}
      style={{ width: trackW, height: size, padding: pad }}
      aria-checked={checked}
      aria-disabled={disabled}
      role="switch"
    >
      <Text component="span"
        className="block rounded-full bg-[var(--surface-primary)] shadow-sm transition-transform duration-200"
        style={{
          width: knob,
          height: knob,
          transform: checked ? `translateX(${travel}px)` : 'translateX(0)',
        }}
      />
    </UnstyledButton>
  );
};

/**
 * Section header for the Facebook/Instagram settings card with brand icons.
 *
 * @param {object} props - Component props.
 * @param {(key: string) => string} props.t - Translation function.
 * @returns {JSX.Element} The rendered header row.
 */
export const Header: React.FC<{ t: (key: string) => string }> = ({ t }) => (
  <div data-testid="facebook-settings-header" className="px-(--space-5) py-(--space-4) flex items-center gap-(--space-3-5) border-b border-(--border-subtle)">
    <div className="flex items-center">
      <FbIcon size={26} />
      <IgIcon size={26} />
    </div>
    <Title order={2} size="lg" className="m-0 font-bold text-(--text-primary)">{t('facebook.headerTitulo')}</Title>
  </div>
);

/**
 * Meta Ads billing status row shown when a payment method is configured.
 *
 * @param {object} props - Component props.
 * @param {FacebookSettings} props.settings - Current Facebook settings.
 * @param {(key: string) => string} props.t - Translation function.
 * @returns {JSX.Element | null} The billing row, or null when no payment method exists.
 */
export const BillingRow: React.FC<{ settings: FacebookSettings; t: (key: string) => string }> = ({ settings, t }) => {
  if (!FF_APP_FACEBOOK_ADS || !settings.fb_has_payment_method) return null;
  return (
    <div data-testid="billing-row" className="px-(--space-4) py-(--space-3) rounded-(--radius-md) border border-(--border-subtle) flex items-center justify-between">
      <div className="flex items-center gap-(--space-2-5)">
        <svg width={15} height={15} viewBox="0 0 24 24" fill="none" stroke="var(--text-tertiary)" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round"><rect x="1" y="4" width="22" height="16" rx="2" ry="2" /><line x1="1" y1="10" x2="23" y2="10" /></svg>
        <Text component="span" className="text-sm text-(--text-secondary)">{t('facebook.faturamentoMetaAds')}</Text>
      </div>
      <Text component="span" className="text-xs font-semibold text-[var(--status-success-text)] bg-[var(--status-success-bg)]/80 px-(--space-2-5) py-(--space-0-5) rounded-full">{t('facebook.configurado')}</Text>
    </div>
  );
};

/**
 * The single row shape used for every network, in every connection state:
 * an icon circle on the left, a title + optional identity/hint text in the
 * middle, an optional trailing control on the right (Connect button, Toggle,
 * or status Badge), and optional meta/footer strips below. Disconnected,
 * connected-with-toggle, and connected-native rows all render through this
 * one component so the card never changes shape between them.
 *
 * @param {object} props - Component props.
 * @param {React.ReactNode} props.icon - The network's brand icon (already sized for the circle).
 * @param {string} [props.iconBg] - CSS background for the icon circle (a brand color); omit for the default neutral bordered circle.
 * @param {string} props.title - The network name ("Facebook" / "Instagram") -- constant regardless of state.
 * @param {string} [props.identity] - Small identity line under the title (page name, @username, "Conectado desde ...").
 * @param {string} [props.hint] - Description/value-pitch paragraph.
 * @param {React.ReactNode} [props.trailing] - The right-aligned control (Button | Toggle | Badge).
 * @param {React.ReactNode} [props.meta] - Optional stats strip, separated by a divider.
 * @param {React.ReactNode} [props.footer] - Optional secondary-actions strip (links, disconnect confirm), separated by a divider.
 * @param {boolean} [props.dimmed] - Renders the row at reduced opacity (e.g. a not-yet-available network).
 * @param {string} [props.testId] - Optional test id for the row.
 * @returns {JSX.Element} The rendered row.
 */
export const NetworkRow: React.FC<{
  icon: React.ReactNode;
  iconBg?: string;
  title: string;
  identity?: string;
  hint?: string;
  trailing?: React.ReactNode;
  meta?: React.ReactNode;
  footer?: React.ReactNode;
  dimmed?: boolean;
  testId?: string;
}> = ({ icon, iconBg, title, identity, hint, trailing, meta, footer, dimmed, testId }) => (
  <div
    data-testid={testId}
    className={cn(
      'rounded-(--radius-lg) bg-(--surface-secondary) border border-(--border-subtle) p-(--space-4)',
      dimmed && 'opacity-60',
    )}
  >
    <div className="flex items-center gap-(--space-3)">
      <div
        className={cn(
          'w-10 h-10 rounded-full flex items-center justify-center shrink-0',
          !iconBg && 'bg-(--surface-primary) border border-(--border-subtle)',
        )}
        style={iconBg ? { background: iconBg } : undefined}
      >
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <Text component="span" className="text-sm font-semibold text-(--text-primary)">{title}</Text>
        {identity && <div className="text-xs text-(--text-tertiary) mt-px">{identity}</div>}
        {hint && <div className="text-sm text-(--text-secondary) mt-(--space-0-5) leading-relaxed">{hint}</div>}
      </div>
      {trailing && <div className="shrink-0">{trailing}</div>}
    </div>
    {meta && <div className="mt-(--space-3) pt-(--space-3) border-t border-(--border-subtle) flex gap-(--space-4)">{meta}</div>}
    {footer && <div className="mt-(--space-3) pt-(--space-3) border-t border-(--border-subtle)">{footer}</div>}
  </div>
);

/**
 * Resolves the StatusBadge tone for a connected network's token remaining lifetime.
 *
 * @param days - Days remaining until expiry, or null when unknown.
 * @returns The tone to use for the token-health badge.
 */
export function tokenHealthTone(days: number | null): StatusBadgeTone {
  if (days === null) return 'neutral';
  if (days <= 10) return 'warning';
  return 'success';
}

/**
 * Resolves the label text for a token-health badge from its tone -- shared
 * by every connected network row (Facebook, Instagram-via-Page, Instagram
 * native) so they all read the same three states the same way. `neutral`
 * (no expiry could be determined) is its own distinct label, not folded
 * into "healthy" -- not knowing is not the same as knowing it's fine.
 *
 * @param tone - The tone from {@link tokenHealthTone}.
 * @param t - Translation function.
 * @returns The label to render inside the badge.
 */
export function tokenHealthLabel(tone: StatusBadgeTone, t: (key: string) => string): string {
  if (tone === 'warning') return t('facebook.statusTokenRenovando');
  if (tone === 'neutral') return t('facebook.statusConexaoInstavel');
  return t('facebook.statusTokenSaudavel');
}

/** A row's disconnect affordance: a link that expands into a full-width inline confirm banner -- the one disconnect pattern shared by every network. */
export const DisconnectFooter: React.FC<{
  confirming: boolean;
  busy?: boolean;
  onTrigger: () => void;
  onCancel: () => void;
  onConfirm: () => void;
  t: (key: string) => string;
  testIdPrefix: string;
  leading?: React.ReactNode;
  /** Overrides the default confirm message -- e.g. to warn that disconnecting also drops a linked network. */
  confirmMessage?: string;
  /** Extra line appended below the confirm message -- e.g. warning that active Lead Ad campaigns will be paused. */
  extraWarning?: string;
}> = ({ confirming, busy, onTrigger, onCancel, onConfirm, t, testIdPrefix, leading, confirmMessage, extraWarning }) => {
  if (confirming) {
    return (
      <div className="w-full p-(--space-3) rounded-(--radius-md) bg-[var(--status-error-subtle)] border border-[var(--status-error-border)] flex items-center justify-between gap-(--space-3)">
        <div className="flex-1 min-w-0">
          <Text component="span" className="text-sm text-[var(--status-error-text)]">{confirmMessage ?? t('facebook.desconectarConfirmacao')}</Text>
          {extraWarning && (
            <Text component="p" className="m-0 mt-(--space-1) text-xs text-[var(--status-error-text)]">{extraWarning}</Text>
          )}
        </div>
        <div className="flex gap-(--space-2) shrink-0">
          <UnstyledButton data-testid={`${testIdPrefix}-cancel-btn`} onClick={onCancel} className="text-xs px-(--space-3) py-(--space-1) rounded-(--radius-sm) border border-(--border-subtle) bg-[var(--surface-primary)] cursor-pointer">{t('facebook.cancelar')}</UnstyledButton>
          <UnstyledButton data-testid={`${testIdPrefix}-confirm-btn`} disabled={busy} onClick={onConfirm} className="text-xs px-(--space-3) py-(--space-1) rounded-(--radius-sm) bg-[var(--status-error)] text-[var(--text-inverse)] border-none cursor-pointer disabled:opacity-60">{t('facebook.desconectar')}</UnstyledButton>
        </div>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-(--space-3)">
      {leading}
      <UnstyledButton data-testid={`${testIdPrefix}-trigger`} onClick={onTrigger} className="text-xs text-(--text-tertiary) underline cursor-pointer bg-transparent border-none p-0">
        {t('facebook.desconectarConta')}
      </UnstyledButton>
    </div>
  );
};
